import type {ReactElement, Ref} from 'react';
import React, {useEffect, useRef} from 'react';
import type {InputFieldProps} from '@Components/input-field-v2';
import {InputField} from '@Components/input-field-v2';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {AiImageFilterPopUp} from '@Panels/stock-panel/components/ai-image-filter-pop-up';
import {
  getAiImageFilterForAspectRatio,
  getAiImageFilterForStylePreset,
  getDropdownItemsForAspectRatioFilterForAIImage,
  getDropdownItemsForStyleFilterForAIImage,
} from '@Panels/stock-panel/stock-panel.library';
import type {AspectRatio, StabilityAIStylePreset} from '@Libraries/pmw-stock-media-library';
import type {AIImageFiltersID} from '@Panels/stock-panel/stock-panel.types';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {AiButton} from '@Components/ai-button';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {Text, TextSize, TextType} from '@Components/text';
import {TextToImageGridErrorState} from '@Components/text-to-image-grid/text-to-image-grid.types';
import {formatNumber, formatToThousand} from '@Utils/number.util';
import styles from './ai-image-prompt.module.scss';

export type InputFieldPropsWithRef = InputFieldProps & {ref?: Ref<HTMLInputElement | HTMLTextAreaElement>};

interface AiImagePromptProps {
  selectedAspectRatio: AspectRatio;
  inputFieldProps: InputFieldPropsWithRef;
  isGenerating: boolean;
  generationButtonText: string;
  errorState: TextToImageGridErrorState;
  className?: string;

  selectedStylePresets?: StabilityAIStylePreset;

  onStyleChanged(stylePreset: AIImageFiltersID): void;

  onAspectRatioChanged(aspectRatio: AIImageFiltersID): void;

  isMobile?: boolean;

  onGenerateButtonClicked(): void;
  remainingUsages?: number;
}

export function AiImagePrompt({isGenerating = false, isMobile = false, ...props}: AiImagePromptProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const isPremium = useRef(typeof getUserId() !== 'undefined' && isUserPremium());

  const getSelectorForStyleFilter = (): ReactElement => {
    return (
      <div>
        <PillButton
          isSelected={!!props.selectedStylePresets}
          isDisabled={isGenerating}
          size={isMobile ? PillButtonSize.SMALL : PillButtonSize.MEDIUM}
          text={props.selectedStylePresets ? getAiImageFilterForStylePreset(props.selectedStylePresets).text : window.i18next.t('pmwjs_style')}
          onClick={() => {}}
        />
      </div>
    );
  };

  const getSelectorForAspectRatioFilter = (): ReactElement => {
    return (
      <div>
        <PillButton
          isSelected={!!props.selectedAspectRatio}
          isDisabled={isGenerating}
          size={isMobile ? PillButtonSize.SMALL : PillButtonSize.MEDIUM}
          text={props.selectedAspectRatio ? getAiImageFilterForAspectRatio(props.selectedAspectRatio).text : window.i18next.t('pmwjs_aspect_ratio')}
          onClick={() => {}}
        />
      </div>
    );
  };

  const getTooltipContent = (): ReactElement => {
    return (
      <div className="flex-center flex-v-row">
        <Text type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: formatNumber(props.remainingUsages ?? 0)})} />
        <Text type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_will_cost_1_credit')} />
      </div>
    );
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div className={`${props.className ?? ''} flex-v-row ${styles.container} ${isMobile ? 'spacing-p-b-1' : ''}`}>
      <InputField
        isDisabled={isGenerating}
        isControlled
        isMultiline
        placeholder={window.i18next.t('pmwjs_describe_image_to_generate')}
        {...(props.inputFieldProps ?? {})}
        ref={inputRef}
        inputClassName={styles.inputField}
      />
      <div className={`flex-h-row flex-justify-between ${styles.filterContainer}`}>
        <div className={`flex-h-row ${styles.filterPillsContainer}`}>
          <Dropdown
            position={DROPDOWN_POSITION.FIXED}
            alignment={ALIGNMENT_TYPE.BOTTOM_START}
            popUpHasCustomWidth
            selector={getSelectorForStyleFilter()}
            popup={
              <AiImageFilterPopUp
                onClick={(id) => {
                  props.onStyleChanged(id);
                }}
                className={styles.stylePopUpListStyle}
                dropdownItems={getDropdownItemsForStyleFilterForAIImage()}
                selectedItemID={props.selectedStylePresets}
              />
            }
            popupSpacingClass="spacing-p-3"
            showModalForSmallScreen={isMobile}
            modalTitle={window.i18next.t('pmwjs_style')}
            popupClassName="border-s-standard"
          />
          <Dropdown
            position={DROPDOWN_POSITION.FIXED}
            alignment={ALIGNMENT_TYPE.BOTTOM_START}
            popUpHasCustomWidth
            selector={getSelectorForAspectRatioFilter()}
            popup={
              <AiImageFilterPopUp
                className="spacing-p-0 spacing-m-0 flex-h-row"
                onClick={(id) => {
                  props.onAspectRatioChanged(id);
                }}
                dropdownItems={getDropdownItemsForAspectRatioFilterForAIImage()}
                selectedItemID={props.selectedAspectRatio}
                imageClasses={styles.filterItemImages}
              />
            }
            popupSpacingClass="spacing-p-3"
            showModalForSmallScreen={isMobile}
            modalTitle={window.i18next.t('pmwjs_aspect_ratio')}
            popupClassName="border-s-standard"
          />
        </div>
        {isMobile ? null : (
          <AiButton
            disabled={props.inputFieldProps.input?.toString().trim().length === 0}
            pillToolTipContent={getTooltipContent()}
            isLoadingButtonOverlayShown
            icon={props.errorState !== TextToImageGridErrorState.NONE ? 'icon-refresh' : 'icon-ai-image'}
            key="ai-image-prompt-btn"
            customClasses="_full-height"
            topPillContent={formatToThousand(props.remainingUsages ?? 0)}
            onClick={() => {
              props.onGenerateButtonClicked();
            }}
            isLoading={isGenerating}
            text={props.generationButtonText}
            variant={!isPremium.current ? AIButtonVariant.PREMIUM : undefined}
            pillClassName={!isPremium.current ? `${styles.premiumPill} spacing-p-l-0 spacing-p-r-0` : 'spacing-p-r-1 spacing-p-l-1 '}
          />
        )}
      </div>
    </div>
  );
}
